import React, { type ReactElement } from "react";
import { graphql } from "react-relay";
import { Link, type RouteComponentProps, withRouter } from "react-router-dom";

import {
	faBoxHeart,
	faBullseye,
	faChartBar,
	faClipboardListCheck,
	faCog,
	faExternalLinkSquareAlt,
	faFileAlt,
	faLightbulb,
	faLock,
	faPlug,
	faSmile,
	faUser,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faRecord, faUsers } from "@awesome.me/kit-4c83d50d0d/icons/kit/custom";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import cx from "classnames";

import { MEETING_AUDIT_FLAG } from "~/account/MeetingAudit";
import AppContext from "~/common/AppContext";
import ViewportContext from "~/common/ViewportContext";
import { useFeature } from "~/lib/flippy";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { isDesktopApp, isNative } from "~/lib/utils";
import { useIsOneOfAIsFeatureFlagEnabled } from "~/meetingRecorder/featureFlag";
import { NavContext } from "~/nav";
import BetaLabel from "~/ui/labels/BetaLabel";
import { HelpCenterLink } from "~/ui/legacyFellowIndex";

import type { SettingsPanelQuery } from "~/__generated__/SettingsPanelQuery.graphql";

import styles from "./SettingsPanel.module.css";

const PanelAdminPowersBlock = () => {
	return (
		<div className={styles.adminPowers}>
			<div>All users on a Free workspace plan are able to perform administrative actions.</div>
			<HelpCenterLink articleId="4088787">Learn more</HelpCenterLink>
		</div>
	);
};

interface PanelItemProps {
	icon?: MenuIcon;
	text: string;
	path: string;
	disabled?: boolean;
	active?: boolean;
	onClick?: () => void;
	iconRight?: MenuIcon;
	useBetaLabel?: boolean;
}
const PanelItem = ({ icon, text, active, path, disabled, onClick, iconRight, useBetaLabel }: PanelItemProps) => {
	let iconElement = null;
	if (icon) {
		// if icon is react element
		if (React.isValidElement(icon)) {
			iconElement = icon;
		} else {
			iconElement = <FontAwesomeIcon icon={icon as IconDefinition} fixedWidth />;
		}
	}

	let iconRightElement = null;
	if (iconRight) {
		// if iconRight is react element
		if (React.isValidElement(iconRight)) {
			iconRightElement = iconRight;
		} else {
			iconRightElement = <FontAwesomeIcon icon={iconRight as IconDefinition} fixedWidth />;
		}
	}

	return (
		<Link
			className={cx(styles.item, { [styles.itemActive]: active, [styles.itemDisabled]: disabled })}
			to={path}
			onClick={disabled ? undefined : onClick}
			data-test-id={active && "active-settings-selection"}
		>
			{iconElement}
			<span>
				{text}
				{useBetaLabel && (
					<>
						&ensp;
						<BetaLabel />
					</>
				)}
			</span>
			{iconRightElement ? <div className={styles.iconRight}>{iconRightElement}</div> : null}
		</Link>
	);
};

type MenuIcon = IconDefinition | ReactElement;

type MenuItem = {
	path: string;
	text: string;
	icon: MenuIcon;
	show?: boolean | null;
	match?: string;
	disabled?: boolean;
	iconRight?: MenuIcon;
	onClick?: () => void;
	useBetaLabel?: boolean;
};

export type SettingsPanelProps = {
	collapse: () => void;
};

const settingsPanelQuery = graphql`
	query SettingsPanelQuery {
		viewer {
			account {
				billingAccount {
					hasAccessToObjectives: hasAccessToFeature(feature: objectives)
					hasAccessToTeams: hasAccessToFeature(feature: teams)
				}
			}
			activeDelegation {
				id
			}
		}
	}
`;

const SettingsPanel = ({ location, collapse }: SettingsPanelProps & RouteComponentProps) => {
	const data = useLazyLoadQuery<SettingsPanelQuery>(settingsPanelQuery, {}, {});
	const appContext = React.useContext(AppContext);
	const { isTablet, isMobile } = React.useContext(ViewportContext);
	const { dispatchPanel } = React.useContext(NavContext);
	const meetingAuditEnabled = useFeatureIsOn(MEETING_AUDIT_FLAG);

	const isOneOfAIsFeatureFlagEnabled = useIsOneOfAIsFeatureFlagEnabled();

	const showAccountSettings = Boolean(appContext.user?.hasAdminAccess);
	const isGuest = Boolean(appContext.user?.isGuest);
	const okrsEnabled = !useFeature("okrs-disabled", "account");
	const { hasAccessToObjectives, hasAccessToTeams } = data.viewer.account.billingAccount;

	const showGuidelinesSettings = Boolean(useFeatureIsOn("extension-prompts") && showAccountSettings && !isNative);

	const accountItems: (MenuItem & { show: boolean })[] = [
		{
			path: "/account/settings/",
			text: "General",
			icon: faCog,
			show: showAccountSettings,
			onClick: collapse,
		},
		{
			path: "/account/security/settings/",
			text: "Security",
			match: "/account/security/",
			icon: faLock,
			show: showAccountSettings,
			onClick: collapse,
		},
		{
			path: "/account/copilot/settings/",
			match: "/account/copilot/",
			text: "Note Taker & AI",
			icon: faRecord,
			show: showAccountSettings && isOneOfAIsFeatureFlagEnabled,
			onClick: collapse,
		},
		{
			path: "/account/defaults",
			text: "Default templates",
			icon: faFileAlt,
			show: showAccountSettings,
			onClick: collapse,
		},
		{
			path: "/account/emoji/",
			text: "Emojis",
			icon: faSmile,
			show: isTablet && showAccountSettings,
			onClick: collapse,
		},
		{
			path: "/account/users/",
			text: "Manage users",
			icon: faUser,
			show: isTablet && showAccountSettings,
			onClick: collapse,
		},
		{
			path: "/account/teams/",
			text: "Manage teams",
			icon: faUsers,
			show: isTablet && showAccountSettings && !!okrsEnabled && hasAccessToTeams,
			onClick: collapse,
		},
		{
			path: "/account/cycles/",
			text: "Objective cycles",
			icon: faBullseye,
			show: Boolean(showAccountSettings && okrsEnabled && hasAccessToObjectives && !isNative),
			onClick: collapse,
		},
		{
			path: "/account/integrations/",
			text: "Integrations",
			icon: faPlug,
			show: Boolean(showAccountSettings && !isNative),
			onClick: collapse,
		},
		{
			path: "/account/suggestions/",
			text: "Employee guidance",
			icon: faLightbulb,
			show: showAccountSettings,
			onClick: collapse,
		},
		{
			path: "/account/meeting-guidelines/",
			text: "Meeting Guidelines",
			icon: faClipboardListCheck,
			show: showGuidelinesSettings,
			onClick: collapse,
		},
		{
			path: "/templates-section/notes/",
			text: "Workspace templates",
			icon: faFileAlt,
			show: showAccountSettings,
			iconRight: faExternalLinkSquareAlt,
			onClick: () => {
				dispatchPanel("templates");
			},
		},
		{
			path: "/account/meeting-audit/",
			text: "Meeting audit",
			icon: faChartBar,
			show: Boolean(showAccountSettings && !isNative && !isDesktopApp && !isMobile && meetingAuditEnabled),
			onClick: collapse,
			useBetaLabel: true,
		},
	];

	const showingAccountItems = accountItems.filter(item => item.show);
	const billingAccount = appContext.account?.billingAccount;
	const showAdminPowersBlurb = billingAccount?.subscriptionState !== "SUBSCRIBED";

	return (
		<section className={styles.panel}>
			<header className={styles.panelHeader}>
				<h1>Workspace Settings</h1>
			</header>
			<main>
				{showingAccountItems.length > 0 && !isGuest && (
					<>
						<div className={styles.section}>
							{showAdminPowersBlurb && <PanelAdminPowersBlock />}
							{showingAccountItems.map(
								({ path, text, icon, disabled, iconRight, onClick, match = null, useBetaLabel }) => {
									return (
										<PanelItem
											key={path}
											path={path}
											icon={icon}
											text={text}
											active={location.pathname.startsWith(match || path)}
											disabled={disabled}
											iconRight={iconRight}
											onClick={onClick}
											useBetaLabel={useBetaLabel}
										/>
									);
								},
							)}
							{Boolean(showAccountSettings && !isNative) && (
								<>
									<hr className={styles.itemsDivider} />
									<PanelItem
										key="/account/billing"
										path="/account/billing"
										icon={faBoxHeart}
										text="Plans & billing"
										active={location.pathname.startsWith("/account/billing")}
										onClick={collapse}
									/>
								</>
							)}
						</div>
					</>
				)}
			</main>
		</section>
	);
};

export default withRouter(SettingsPanel);
